import React, { useCallback, useMemo, useEffect, useRef } from 'react';
import { gql } from '@apollo/client';

import appConfig from '@nerdwallet/app-config';

import { useQueryWithLog } from '@lib/hooks/logging-hooks';
import { getLogger } from '@lib/logging';
import type { CanopyConnectHandler } from '@lib/types/insurance';

import type {
  OnAuthenticationSuccessEventData,
  OnExitError,
  OnExitEventData,
} from './types';

const logger = getLogger('CanopyConnect');

// Canopy recommends loading the SDK from their CDN
// https://docs.usecanopy.com/reference/using-the-sdk#installation
const CANOPY_CONNECT_SDK_URL = 'https://cdn.usecanopy.com/v2/canopy-connect.js';
const CANOPY_EMBED_SCRIPT_ID = 'embed-canopy-script';
const CANOPY_CONNECT_SELECTOR = `#${CANOPY_EMBED_SCRIPT_ID}`;
const CANOPY_PUBLIC_ALIAS = appConfig?.CANOPY_PUBLIC_ALIAS;

export const GET_ENCRYPTED_IDENTITY_ID = gql`
  query GetCanopyIdentityId {
    canopyIdentityId
  }
`;

type CanopyConnectProps = {
  /** Additional metadata to pass to the CanopyConnect widget and associate with the pull */
  additionalMetadata?: Record<string | number | symbol, any>;
  /** Used to inform the CanopyConnect widget that the user is reconnecting an existing account */
  reconnectToken?: string;
  /** Callback method to handle when an Insurance Carrier has been authenticated */
  onAuthenticationSuccess?: (data: OnAuthenticationSuccessEventData) => void;
  /** Callback method to handle when the Canopy Connect flow is exited. For modals this is when the modal is closed. For embeds this is when the final success screen is shown. */
  onExit?: (err: OnExitError, data: OnExitEventData) => void;
};

const CanopyConnect = ({
  additionalMetadata,
  reconnectToken,
  onAuthenticationSuccess,
  onExit,
}: CanopyConnectProps): React.ReactNode => {
  const {
    data,
    loading: encryptedIdentityIdLoading,
    error: encryptedIdentityIdError,
  } = useQueryWithLog(logger, GET_ENCRYPTED_IDENTITY_ID);
  const canopyEncryptedIdentityId = data?.canopyIdentityId;

  const metadata = useMemo(
    () => ({
      ...additionalMetadata,
      encrypted_identity_id: canopyEncryptedIdentityId,
      should_show_rate_report: true,
    }),
    [additionalMetadata, canopyEncryptedIdentityId],
  );

  // Use ref instead of state because we don't want the component to re-render
  // because the canopyConnectHandler is being assigned.
  const canopyConnectHandler = useRef<CanopyConnectHandler>(null);

  const handleLoaded = useCallback(() => {
    if (canopyConnectHandler.current) return;

    canopyConnectHandler.current = window.CanopyConnect.create({
      publicAlias: CANOPY_PUBLIC_ALIAS,
      modal: true,
      pullMetaData: metadata,
      reconnectToken,
    });

    if (onAuthenticationSuccess) {
      canopyConnectHandler.current.on(
        'authenticationSuccess',
        onAuthenticationSuccess,
      );
    }

    if (onExit) {
      canopyConnectHandler.current.on('exit', onExit);
    }

    canopyConnectHandler.current.open();
  }, [metadata, reconnectToken, onAuthenticationSuccess, onExit]);

  useEffect(() => {
    if (encryptedIdentityIdError) {
      logger.error(
        'Failed to fetch the encrypted Canopy Identity ID',
        encryptedIdentityIdError,
      );
    }
  }, [encryptedIdentityIdError]);

  useEffect(() => {
    if (encryptedIdentityIdLoading || encryptedIdentityIdError) {
      return;
    }

    const existingCanopyScript = document.querySelectorAll(
      CANOPY_CONNECT_SELECTOR,
    );

    if (existingCanopyScript.length === 0) {
      const canopyScriptElement: HTMLScriptElement =
        document.createElement('script');

      canopyScriptElement.id = CANOPY_EMBED_SCRIPT_ID;
      canopyScriptElement.src = CANOPY_CONNECT_SDK_URL;
      canopyScriptElement.onload = handleLoaded;

      document.body.appendChild(canopyScriptElement);
    }

    return () => {
      if (canopyConnectHandler.current) {
        canopyConnectHandler.current.destroy();
        canopyConnectHandler.current = null;
      }

      document.querySelector(CANOPY_CONNECT_SELECTOR).remove();
    };
  }, [
    encryptedIdentityIdError,
    encryptedIdentityIdLoading,
    handleLoaded,
    metadata,
  ]);

  return null;
};

export default CanopyConnect;
